import axios, { type InternalAxiosRequestConfig, type AxiosError, type AxiosResponse } from "axios";
import { getUserToken } from "@/lib/firebase";

const apiClient = axios.create({
  baseURL: String(import.meta.env.VITE_API_BASE_URL ?? ""),
  timeout: 30000, // 30 seconds timeout
});

// Request interceptor
apiClient.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    const token = await getUserToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Log request in development mode
    if (import.meta.env.DEV) {
      console.log(`[API Request] ${config.method?.toUpperCase() ?? ""} ${config.url ?? ""}`, {
        params: config.params as unknown,
        data: config.data as unknown,
      });
    }

    return config;
  },
  (error: unknown) => {
    return Promise.reject(error instanceof Error ? error : new Error(String(error)));
  },
);

// Response interceptor
apiClient.interceptors.response.use(
  (response: AxiosResponse) => {
    // Log response in development mode
    if (import.meta.env.DEV) {
      console.log(
        `[API Response] ${response.config.method?.toUpperCase() ?? ""} ${response.config.url ?? ""}`,
        {
          status: response.status,
          data: response.data as unknown,
        },
      );
    }
    return response;
  },
  async (error: AxiosError<{ message?: string; error?: string }>) => {
    // Log error in development mode
    if (import.meta.env.DEV) {
      console.error(
        `[API Error] ${error.config?.method?.toUpperCase() ?? ""} ${error.config?.url ?? ""}`,
        {
          status: error.response?.status,
          data: error.response?.data,
          message: error.message,
        },
      );
    }

    return Promise.reject(error);
  },
);

export default apiClient;
